// Componente provider-select condiviso (tenant + master).
// Estratto da packages/frontend/src/components/ai/ServiceProviderSelect.tsx
// per permettere riuso master senza duplicazione.
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@easyflow/ui-frontend';
import type { ProviderMetaEntry } from './types';

const FALLBACK_META: ProviderMetaEntry = { label: 'Disattivato', dotColor: 'bg-gray-400' };

/**
 * Descrittore live di un provider dal payload backend
 * `/ai-tools/available-providers`. Esportato per essere riutilizzato dai
 * chiamanti (es. `ServiceRoutingTab`) e per centralizzare la logica di
 * mapping `is_valid`/`reason_code` → colore del dot.
 */
export interface AvailableProviderEntry {
  id: string;
  label: string;
  kind: 'canonical' | 'custom';
  capability_supported: boolean;
  /** Live flag (canonical: ai_providers.is_valid; custom: health!=down + include_in_routing). */
  is_valid: boolean;
  disabled_reason: string | null;
  reason_code?:
    | 'CAPABILITY_UNSUPPORTED'
    | 'ENDPOINT_INACTIVE'
    | 'EXCLUDED_FROM_ROUTING'
    | 'HEALTH_DOWN'
    | 'INTERNAL_ERROR'
    | null;
  health_status?: string;
}

/**
 * Stato della lista provider di una riga. Il backend
 * (`/ai-tools/available-providers`) è l'unica sorgente: finché la lista non è
 * arrivata la select resta disabilitata, su errore mostra il motivo e un retry.
 * Non esiste un ripiego client-side: una lista costruita nel FE non conosce né
 * le capability del service_key né gli endpoint custom del tenant.
 */
export type ProviderAvailability =
  | { status: 'loading' }
  | { status: 'error'; message: string; onRetry?: () => void }
  | { status: 'ready'; providers: ReadonlyArray<AvailableProviderEntry> };

/**
 * Mappa lo stato live di un provider sul colore del dot:
 *   - verde (`bg-emerald-500`) → provider operativo (`is_valid=true`);
 *   - rosso (`bg-red-500`)     → provider configurato ma in failure
 *                                 (`HEALTH_DOWN` / `INTERNAL_ERROR`);
 *   - grigio (`bg-gray-400`)    → provider non configurato / disattivato
 *                                 (`ENDPOINT_INACTIVE` / `EXCLUDED_FROM_ROUTING`
 *                                 o `is_valid=false` senza reason_code).
 * Il brand "kind" (cyan per custom) resta come accent ring intorno al dot
 * — vedi `liveRingClass`.
 */
function liveDotColor(entry: AvailableProviderEntry): string {
  if (entry.is_valid) return 'bg-emerald-500';
  if (entry.reason_code === 'HEALTH_DOWN' || entry.reason_code === 'INTERNAL_ERROR') {
    return 'bg-red-500';
  }
  return 'bg-gray-400';
}

function liveRingClass(entry: AvailableProviderEntry): string {
  // Accent visivo per distinguere canonical vs custom mantenendo lo stato
  // live nel colore del dot.
  return entry.kind === 'custom' ? 'ring-1 ring-cyan-400 ring-offset-1 ring-offset-background' : '';
}

function liveTooltip(entry: AvailableProviderEntry): string | undefined {
  if (entry.disabled_reason) return entry.disabled_reason;
  if (entry.is_valid) return 'Operativo';
  if (entry.health_status && entry.health_status !== 'ok') {
    return `Stato: ${entry.health_status}`;
  }
  return undefined;
}
const DEFAULT_PROVIDER_META: Record<string, ProviderMetaEntry> = {
  openai: { label: 'OpenAI', dotColor: 'bg-sky-500' },
  claude: { label: 'Claude', dotColor: 'bg-purple-500' },
  google: { label: 'Gemini', dotColor: 'bg-blue-500' },
  none:   FALLBACK_META,
};

export interface ServiceProviderSelectProps {
  serviceKey: string;
  currentProvider: string;
  /** Lista provider della riga (sorgente: backend) con il suo stato di caricamento. */
  availability: ProviderAvailability;
  /**
   * Se il servizio può essere spento (`'none'`). L'opzione non arriva dal
   * backend perché non è un provider: la decide la riga.
   */
  allowNone?: boolean;
  onChange: (provider: string) => void | Promise<void>;
  saving?: boolean;
  disabled?: boolean;
  disabledReasons?: Partial<Record<string, string>>;
  widthClass?: string;
  providerMeta?: Record<string, ProviderMetaEntry>;
  /**
   * Pattern UX uniforme: toggle ON/OFF a sinistra del select.
   * - OFF → emette `onChange('none')`, dropdown provider nascosto.
   * - ON  → emette `onChange(default)` dove default = primo provider
   *         disponibile != 'none' non bloccato.
   * Quando il servizio è ON viene mostrato il dropdown coi soli provider
   * reali (l'opzione 'none' è gestita dal toggle, mai dalla select).
   */
  withToggle?: boolean;
}

interface ProviderOptionsProps {
  providers: ReadonlyArray<string>;
  resolveMeta: (id: string) => ProviderMetaEntry;
  blockReasonFor: (id: string) => string | undefined;
  tooltipFor: (id: string) => string | undefined;
  dotClassFor: (id: string) => string;
  ringClassFor: (id: string) => string;
}

function ProviderOptions({ providers, resolveMeta, blockReasonFor, tooltipFor, dotClassFor, ringClassFor }: ProviderOptionsProps) {
  return (
    <SelectContent>
      {providers.map((p) => {
        const pm: ProviderMetaEntry = resolveMeta(p);
        const blockReason = blockReasonFor(p);
        return (
          <SelectItem
            key={p}
            value={p}
            className="text-xs"
            disabled={!!blockReason}
            title={tooltipFor(p)}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${dotClassFor(p)} ${ringClassFor(p)}`} />
              {pm.label}
              {blockReason && (
                <span className="text-[9px] text-gray-400">(non supportato)</span>
              )}
            </div>
          </SelectItem>
        );
      })}
    </SelectContent>
  );
}

interface AvailabilityErrorProps {
  serviceKey: string;
  message: string;
  onRetry?: () => void;
}

/** Stato esplicito quando la lista provider non è disponibile: niente select, motivo + retry. */
function AvailabilityError({ serviceKey, message, onRetry }: AvailabilityErrorProps) {
  return (
    <div
      className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
      role="status"
      data-testid={`provider-select-${serviceKey}-unavailable`}
      title={message}
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>Provider non caricati</span>
      {onRetry && (
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onRetry}>
          Riprova
        </Button>
      )}
    </div>
  );
}

export function ServiceProviderSelect({
  serviceKey,
  currentProvider,
  availability,
  allowNone = false,
  onChange,
  saving = false,
  disabled = false,
  disabledReasons,
  widthClass = 'w-[140px]',
  providerMeta = DEFAULT_PROVIDER_META,
  withToggle = false,
}: ServiceProviderSelectProps) {
  const backendProviders: ReadonlyArray<AvailableProviderEntry> =
    availability.status === 'ready' ? availability.providers : [];
  const backendMetaById = new Map<string, ProviderMetaEntry>();
  const backendReasons: Record<string, string> = {};
  // Indice per id del payload backend per accedere a `is_valid` /
  // `reason_code` / `health_status` durante il render del dot e del tooltip.
  const backendEntryById = new Map<string, AvailableProviderEntry>();
  for (const ap of backendProviders) {
    backendEntryById.set(ap.id, ap);
    if (!providerMeta[ap.id]) {
      // Entry custom (id = `custom:<uuid>`): non presente in DEFAULT_PROVIDER_META
      // → label dal backend, dotColor placeholder (sovrascritto da `liveDotColor`).
      backendMetaById.set(ap.id, { label: ap.label, dotColor: 'bg-gray-400' });
    }
    const r = ap.disabled_reason;
    if (r) backendReasons[ap.id] = r;
  }
  const resolveMeta = (id: string): ProviderMetaEntry =>
    providerMeta[id] ?? backendMetaById.get(id) ?? providerMeta.none ?? FALLBACK_META;
  const meta = resolveMeta(currentProvider);

  // Il colore del dot riflette lo stato operazionale (`is_valid` /
  // `reason_code`) invece del brand color statico. Per i provider non
  // rappresentati nel payload (es. `none`) ricadiamo sulla `ProviderMetaEntry.dotColor`.
  const dotClassFor = (id: string): string => {
    const entry = backendEntryById.get(id);
    if (entry) return liveDotColor(entry);
    return resolveMeta(id).dotColor;
  };
  const ringClassFor = (id: string): string => {
    const entry = backendEntryById.get(id);
    return entry ? liveRingClass(entry) : '';
  };
  const blockReasonFor = (id: string): string | undefined => disabledReasons?.[id] ?? backendReasons[id];
  const tooltipFor = (id: string): string | undefined => {
    const entry = backendEntryById.get(id);
    const live = entry ? liveTooltip(entry) : undefined;
    return blockReasonFor(id) ?? live;
  };
  const currentDot = dotClassFor(currentProvider);
  const currentRing = ringClassFor(currentProvider);
  const currentTooltip = tooltipFor(currentProvider);

  // Filtro strutturato via `reason_code`: nascondi SOLO i provider con
  // capability non supportata (canonical o custom). Quelli con stati
  // operazionali transitori (ENDPOINT_INACTIVE / EXCLUDED_FROM_ROUTING /
  // HEALTH_DOWN / INTERNAL_ERROR) restano visibili ma disabled con
  // `disabled_reason` come tooltip, così l'operatore vede il motivo e può agire.
  const available: string[] = backendProviders
    .filter((ap) => ap.capability_supported || ap.reason_code !== 'CAPABILITY_UNSUPPORTED')
    .map((ap) => ap.id)
    .concat(allowNone ? ['none'] : []);
  // Quando il pattern toggle è attivo, l'opzione 'none' NON deve apparire
  // nella select: lo stato OFF è governato dallo Switch a sinistra.
  const realProviders = withToggle ? available.filter((p) => p !== 'none') : available;
  const isLoading = availability.status === 'loading';
  const triggerDisabled = disabled || saving || isLoading || realProviders.length <= 1;

  const optionsProps: ProviderOptionsProps = {
    providers: realProviders,
    resolveMeta,
    blockReasonFor,
    tooltipFor,
    dotClassFor,
    ringClassFor,
  };

  const triggerValue = saving || isLoading ? (
    <Loader2 className="h-3 w-3 animate-spin" aria-label={isLoading ? 'Caricamento provider' : 'Salvataggio'} />
  ) : (
    <SelectValue>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${currentDot} ${currentRing}`} />
        <span>{meta.label}</span>
      </div>
    </SelectValue>
  );

  // Pattern toggle uniforme: Switch ON/OFF a sinistra + select provider a destra.
  // Stato derivato unicamente da `currentProvider` (single source of truth):
  // none → OFF, qualsiasi altro valore → ON.
  if (withToggle) {
    const isOn = currentProvider !== 'none';
    const enableProvider = (): void => {
      const next = realProviders.find((p) => !blockReasonFor(p));
      if (!next) return;
      void onChange(next);
    };
    const handleToggle = (checked: boolean): void => {
      if (checked === isOn) return;
      if (checked) enableProvider();
      else void onChange('none');
    };
    // Senza lista backend non si può accendere (non sapremmo quale provider);
    // spegnere resta sempre possibile.
    const canEnable = availability.status === 'ready' && realProviders.some((p) => !blockReasonFor(p));
    const switchDisabled = disabled || saving || (!isOn && !canEnable);
    return (
      <div className="flex items-center gap-2">
        <Switch
          checked={isOn}
          onCheckedChange={handleToggle}
          disabled={switchDisabled}
          data-testid={`provider-toggle-${serviceKey}`}
          aria-label={isOn ? 'Disattiva servizio' : 'Attiva servizio'}
        />
        {availability.status === 'error' ? (
          <AvailabilityError serviceKey={serviceKey} message={availability.message} onRetry={availability.onRetry} />
        ) : isOn && (
          <Select
            value={currentProvider}
            onValueChange={(v: string) => { void onChange(v); }}
            disabled={triggerDisabled}
          >
            <SelectTrigger
              className={`${widthClass} h-8 text-xs`}
              data-testid={`provider-select-${serviceKey}`}
              title={currentTooltip}
            >
              {triggerValue}
            </SelectTrigger>
            <ProviderOptions {...optionsProps} />
          </Select>
        )}
      </div>
    );
  }

  if (availability.status === 'error') {
    return <AvailabilityError serviceKey={serviceKey} message={availability.message} onRetry={availability.onRetry} />;
  }

  return (
    <Select value={currentProvider} onValueChange={(v: string) => { void onChange(v); }} disabled={triggerDisabled}>
      <SelectTrigger
        className={`${widthClass} h-8 text-xs`}
        data-testid={`provider-select-${serviceKey}`}
        title={currentTooltip}
      >
        {triggerValue}
      </SelectTrigger>
      <ProviderOptions {...optionsProps} />
    </Select>
  );
}

export default ServiceProviderSelect;
